import React from 'react';
import { useTranslation } from 'react-i18next';
import QueryObjectTable from 'mrf/view/QueryObjectTable';
import { Col, Grid } from 'mrf/view/Grid';
import FetcherObjectSelect from 'mrf/view/FetcherObjectSelect';
import { useFormFullSelector, useFormSelector } from 'mrf/redux/ReduxHelper';
import { daoPost, daoDelete } from 'mrf/Dao';
import store from 'mrf/redux/store';
import Modal, { useModal } from 'mrf/view/Modal';
import { DialogTitle, DialogActions, DialogContent } from 'mrf/view/Dialog';
import Button from 'mrf/view/Button';
import SectionTitle from 'mrf/view/SectionTitle';
import Label from 'styles/basic/Label';
import useCounter from 'mrf/utils/useCounter';
import T from 'mrf/utils/T';
import mrf from 'mrf.config';
import If from 'mrf/utils/If';
import Else from 'mrf/utils/Else';
import QueryObjectField from 'mrf/view/QueryObjectField';

/*
NOTE: by "CALLER" we mean any classes/react components  which uses this
component, i.e., the parent element
*/

export default function FormQueryObjectTable({
    // the unique indentifier of the owning entity (generally, the CALLER itself)
    ownerId,

    // table properties
    tableResource,
    tableLabel,
    tableFields,
    relationshipResource = tableResource,

    // general properties
    relationshipDescriptor,
    endpoint=`api/${tableResource}`,
    queryEndpoint=endpoint,
    field,
    selectEventMapper,

    // modal properties
    modalResource,
    modalTitle,
    modalDisplayAttributeName,
    modalEndpoint,
    modalQuery,
    modalCondition,

    // callbacks needed to build queries
    formMap,
    confirmDelete,

    // personalized messages
    addSuccess='Item successfully added',
    addFailure='Failed to add item',
    deleteSuccess='Item successfully removed',
    deleteFailure='Failed to remove item',

    // layout options
    useSectionTitle,
    width='1/3',

    useQueryObjectField=false,
    queryObjectFields,

    disableRemove=false,

    ...props
}) {
    const dialog = useModal();
    const { t } = useTranslation();

    const uiContext = useFormSelector({
        resource: tableResource + 'FormUi'
    });

    const { data: ui, set: setUiValue } = useFormFullSelector(uiContext, field);

    const [ tableCounter, reloadTable ] = useCounter();

    const getRelationshipId = idx => {
        return store.getState()?.resources?.[relationshipResource + 'Query']?.list?.[idx];
    }

    // get owned entity data using the MxN relationship id
    const getOwnedEntity = relationshipId => {
        const resources = store.getState()?.resources;
        const ownedEntityId = resources?.[relationshipResource]?.byId?.[relationshipId]?.[field];

        return resources?.[modalResource]?.byId?.[ownedEntityId];
    }

    const addCb = async e => {
        e.preventDefault();

        try {
            await daoPost(endpoint, formMap(ui));

            setUiValue(null);
            reloadTable();

            dialog?.close?.(); // eslint-disable-line
            alert(t(addSuccess));
        } catch (e) {
            alert(t(addFailure)); // FIXME: [CDI-443] Better error handling for UserRoleView.js
            console.error(e);
        }
    }

    const removeCb = async (idx) => {
        const relationshipId = getRelationshipId(idx);

        if (!confirmDelete?.(getOwnedEntity(relationshipId))) {
            return;
        }

        try {
            mrf.usePostForDelete ? await daoPost(endpoint + `/${relationshipId}/delete`, '') 
                : await daoDelete(endpoint + `/${relationshipId}`);

            setUiValue(null);
            reloadTable();

            alert(t(deleteSuccess));
        } catch (e) {
            alert(t(deleteFailure));
            console.error(e);
        }
    }

    return (
        <Grid>
            <Col size={12}>
                {useSectionTitle
                    ? <SectionTitle>{tableLabel}</SectionTitle>
                    : <Label><T>{tableLabel}</T></Label>
                }
                <QueryObjectTable
                    key={tableCounter}
                    resource={tableResource}
                    endpoint={queryEndpoint}
                    extraQueryParams={ownerId ? { id: ownerId } : null}
                    descriptor={relationshipDescriptor}
                    field={field}
                    fields={tableFields}
                    addCb={dialog?.open}
                    removeCb={!disableRemove && removeCb}
                    {...props}
                />
            </Col>

            <Modal {...dialog} width={width}>
                <DialogTitle>{modalTitle}</DialogTitle>
                <DialogContent>
                    <Grid>
                        <Col size={12}>
                            <If condition={useQueryObjectField}>
                                <QueryObjectField
                                    field={field}
                                    resource={modalResource}
                                    endpoint={modalEndpoint}
                                    query={modalQuery}
                                    condition={modalCondition}
                                    displayAttribute={modalDisplayAttributeName}
                                    context={uiContext}
                                    fields={queryObjectFields}
                                    {...props}
                                />
                            </If>
                            <Else condition={useQueryObjectField}>
                                <FetcherObjectSelect
                                    field={field}
                                    resource={modalResource}
                                    endpoint={modalEndpoint}
                                    query={modalQuery}
                                    condition={modalCondition}
                                    displayAttribute={modalDisplayAttributeName}
                                    context={uiContext}
                                    selectEventMapper={selectEventMapper}
                                />
                            </Else>
                        </Col>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button color='primary' onClick={addCb}>Ok</Button>
                </DialogActions>
            </Modal>
        </Grid>
    );
}
